<template>
    <div class="space-y-4">
        <!-- Onboarding Card: visible until Plaid identity verification is approved -->
        <div
            v-if="$page.props.user.role === 'agent' && tabData?.transfer_diligence_status !== 'approved' && tabData?.subscription_data?.has_access"
            class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
        >
            <div class="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div class="flex items-start gap-4">
                    <div class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gray-100">
                        <font-awesome-icon :icon="faBuildingColumns" class="h-6 w-6 text-gray-600" />
                    </div>
                    <div>
                        <h3 class="text-lg font-bold text-gray-900">Set up rent payments to get paid</h3>
                        <p class="mt-1 text-sm leading-relaxed text-gray-500">
                            Connect your bank account to accept rent payments through Plaid.
                            Then, invite your tenant to set up secure rent transfers.
                        </p>
                    </div>
                </div>
                <div class="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                    <a
                        v-if="!isSharedViewOnly"
                        :href="route('payment-methods.index') + '#rent-payments-setup'"
                        class="inline-flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-700"
                        @click="onConnectBank"
                    >
                        <font-awesome-icon :icon="faBuildingColumns" class="h-4 w-4" />
                        Connect Bank
                    </a>
                    <span v-else class="max-w-xs text-sm font-medium text-gray-500 sm:text-right">
                        Only the listing agent can connect a bank account.
                    </span>
                    <div class="flex items-center gap-1.5 text-xs text-gray-400">
                        <font-awesome-icon :icon="faLock" class="h-3 w-3" />
                        Secure &amp; encrypted
                    </div>
                </div>
            </div>

            <!-- Steps -->
            <div class="mt-6 border-t border-gray-100 pt-5">
                <!-- Desktop -->
                <div class="hidden items-start gap-3 md:flex">
                    <template v-for="(step, index) in onboardingSteps" :key="step.title">
                        <div class="flex flex-1 items-start gap-3">
                            <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-xs font-semibold text-gray-600">
                                {{ index + 1 }}
                            </div>
                            <div>
                                <p class="text-sm font-semibold text-gray-900">{{ step.title }}</p>
                                <p class="mt-0.5 text-xs leading-relaxed text-gray-500">{{ step.description }}</p>
                            </div>
                        </div>
                        <div v-if="index < onboardingSteps.length - 1" class="mt-2 shrink-0 text-gray-400" aria-hidden="true">
                            <font-awesome-icon :icon="faArrowRight" class="h-4 w-4" />
                        </div>
                    </template>
                </div>
                <!-- Mobile -->
                <div class="grid grid-cols-1 gap-3 md:hidden">
                    <div
                        v-for="(step, index) in onboardingSteps"
                        :key="`m-${step.title}`"
                        class="flex items-start gap-3"
                    >
                        <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-xs font-semibold text-gray-600">
                            {{ index + 1 }}
                        </div>
                        <div>
                            <p class="text-sm font-semibold text-gray-900">{{ step.title }}</p>
                            <p class="mt-0.5 text-xs leading-relaxed text-gray-500">{{ step.description }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tenant & Lease Card -->
        <div class="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div class="p-5">
                <div class="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                    <!-- Tenant Info -->
                    <div class="flex min-w-0 items-center gap-4">
                        <div class="flex h-14 w-14 shrink-0 select-none items-center justify-center rounded-full bg-gray-200 text-sm font-bold text-gray-600">
                            {{ tenantInitials }}
                        </div>
                        <div class="min-w-0">
                            <p class="truncate text-xl font-bold text-gray-900">
                                {{ rentPaymentData?.tenant?.name || rentPaymentData?.invitation?.email || 'No tenant assigned' }}
                            </p>
                            <div v-if="rentPaymentData?.rent_info" class="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500">
                                <font-awesome-icon :icon="faCalendarDays" class="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                Lease Term: {{ rentPaymentData.rent_info.lease_start_date }} – {{ rentPaymentData.rent_info.lease_end }}
                            </div>
                            <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
                                <span
                                    v-if="leaseTermStatus === 'Active'"
                                    class="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700"
                                >
                                    <span class="h-1.5 w-1.5 rounded-full bg-green-500" aria-hidden="true" />
                                    Active Lease
                                </span>
                                <span
                                    v-else-if="leaseTermStatus === 'Rent Payments Stopped'"
                                    class="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700"
                                >
                                    Rent Payments Stopped
                                </span>
                                <span
                                    v-else-if="leaseTermStatus === 'Complete'"
                                    class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600"
                                >
                                    Lease Complete
                                </span>
                                <!-- Invitation sent but tenant hasn't registered yet -->
                                <span
                                    v-if="rentPaymentData?.invitation && !rentPaymentData?.tenant?.name"
                                    class="inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700"
                                >
                                    <font-awesome-icon :icon="faEnvelope" class="h-3 w-3" />
                                    Invitation pending
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Monthly Rent -->
                    <div class="shrink-0 lg:border-l lg:border-gray-200 lg:pl-6">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Monthly Rent</p>
                        <p class="mt-0.5 text-3xl font-bold text-gray-900">${{ rentPaymentData?.rent || property.rent || '0.00' }}</p>
                        <div v-if="isPastDue && pastDueAmount > 0" class="mt-1 text-sm font-medium text-red-600">
                            Past Due: ${{ pastDueAmount.toFixed(2) }}
                        </div>
                        <div v-else class="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                            <font-awesome-icon :icon="faCalendarDays" class="h-3 w-3 text-gray-400" />
                            Collected monthly
                        </div>
                    </div>

                    <!-- Payment Status Indicator -->
                    <div v-if="hasLeaseTerm" class="shrink-0 lg:pl-4">
                        <div v-if="isBankVerified" class="flex items-start gap-2.5">
                            <font-awesome-icon :icon="faCircleCheck" class="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
                            <div>
                                <p class="text-sm font-semibold text-gray-900">Rent payments are active</p>
                                <p class="mt-0.5 text-xs leading-relaxed text-gray-500">Payments will be collected on time each month.</p>
                            </div>
                        </div>
                        <div v-else class="flex items-start gap-2.5">
                            <font-awesome-icon :icon="faCircleExclamation" class="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                            <div>
                                <p class="text-sm font-semibold text-gray-900">Bank account not connected</p>
                                <p class="mt-0.5 text-xs leading-relaxed text-gray-500">Connect your bank to start collecting rent payments.</p>
                            </div>
                        </div>
                    </div>

                    <!-- CTAs -->
                    <div class="shrink-0">
                        <!-- Shared view only -->
                        <div v-if="isSharedViewOnly" class="flex items-center gap-2 text-sm text-gray-500">
                            <span>Rent Payments setup is managed by the listing agent.</span>
                            <ToolTip
                                v-if="viewOnlyTooltip"
                                :title="viewOnlyTooltip.title"
                                :description="viewOnlyTooltip.description"
                            />
                        </div>

                        <!-- Agent CTAs -->
                        <div v-else class="flex w-full flex-col gap-2 sm:w-44">
                            <!-- Primary CTA. 86bcd9hg3: without rent-payments access, inviting a
                                 tenant opens the Pro pop-up here instead of a page that only says so. -->
                            <button
                                v-if="!hasRentPaymentsEntitlement && !hasEditableLeaseTerm"
                                type="button"
                                data-test="rent-payments-invite-upgrade"
                                class="flex items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                                @click="emit('request-upgrade')"
                            >
                                {{ isCompletedLeaseTerm ? 'Invite New Tenant' : 'Invite Tenant to Payments' }}
                            </button>
                            <Link
                                v-else-if="isCompletedLeaseTerm"
                                :href="route('rented-properties.create-lease-information', property.id)"
                                class="flex items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                            >
                                Invite New Tenant
                            </Link>
                            <Link
                                v-else-if="hasEditableLeaseTerm && isBankVerified"
                                :href="route('rented-properties.edit-lease', rentPaymentData.rent_info.id)"
                                class="flex items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                            >
                                <font-awesome-icon icon="fa-edit" class="h-4 w-4" />
                                Edit Payments
                            </Link>
                            <button
                                v-else-if="hasEditableLeaseTerm && !isBankVerified && rentPaymentData?.invitation"
                                type="button"
                                class="flex items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                                @click="showResendInvitationPopup(rentPaymentData.rent_info?.id)"
                            >
                                <font-awesome-icon :icon="faEnvelope" class="h-4 w-4" />
                                Resend Invitation
                            </button>
                            <Link
                                v-else-if="hasEditableLeaseTerm && !isBankVerified"
                                :href="route('rented-properties.edit-lease', rentPaymentData.rent_info.id)"
                                class="flex items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                            >
                                Invite Tenant
                            </Link>
                            <Link
                                v-else
                                :href="route('rented-properties.create-lease-information', property.id)"
                                class="flex items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                            >
                                Invite Tenant to Payments
                            </Link>
                        </div>
                    </div>
                </div>

                <!-- Completed Lease Notice -->
                <div
                    v-if="isCompletedLeaseTerm && !isSharedViewOnly"
                    class="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
                >
                    <p class="text-sm leading-relaxed text-amber-900">
                        This lease term is complete. Use
                        <span class="font-semibold">Invite New Tenant</span>
                        to set up rent payments for the next tenant.
                    </p>
                </div>
            </div>
        </div>

        <!-- Payout Account Card: visible once Plaid is verified and agent has bank accounts -->
        <div
            v-if="$page.props.user.role === 'agent' && isBankVerified && agentBankAccounts.length > 0 && !isSharedViewOnly"
            class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
        >
            <!-- Display mode -->
            <div v-if="!showBankAccountSelector" class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex items-center gap-4">
                    <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100">
                        <font-awesome-icon :icon="faBuildingColumns" class="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Payout Account</p>
                        <p v-if="currentBankAccount" class="mt-0.5 text-base font-semibold text-gray-900">
                            {{ currentBankAccount.bank_name }} ••••{{ currentBankAccount.account_last_4 }}<template v-if="currentBankAccount.account_name"> ({{ currentBankAccount.account_name }})</template>
                        </p>
                        <p v-else class="mt-0.5 text-sm text-gray-400">No account selected</p>
                        <div v-if="currentBankAccount" class="mt-1 flex items-center gap-1.5 text-xs font-medium text-green-700">
                            <font-awesome-icon :icon="faCircleCheck" class="h-3.5 w-3.5" />
                            Currently active
                        </div>
                        <p v-else class="mt-1 text-xs text-amber-600">Select an account to receive payouts.</p>
                    </div>
                </div>
                <button
                    type="button"
                    class="inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 sm:w-44 sm:self-center"
                    @click="showBankAccountSelector = true"
                >
                    <font-awesome-icon :icon="faArrowRightArrowLeft" class="h-4 w-4" />
                    Change Account
                </button>
            </div>

            <!-- Selector mode -->
            <div v-else class="flex flex-col gap-3 sm:flex-row sm:items-center">
                <select
                    v-model="selectedBankAccountId"
                    class="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:w-80"
                >
                    <option :value="null" disabled>Select a bank account</option>
                    <option
                        v-for="account in agentBankAccounts"
                        :key="account.id"
                        :value="account.id"
                    >
                        {{ account.bank_name }} ****{{ account.account_last_4 }}<template v-if="account.account_name"> ({{ account.account_name }})</template>
                    </option>
                </select>
                <div class="flex items-center gap-2">
                    <button
                        v-if="selectedBankAccountId !== currentBankAccountId"
                        type="button"
                        :disabled="isSavingBankAccount"
                        class="inline-flex items-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-500 disabled:opacity-50"
                        @click="updateBankAccount"
                    >
                        {{ isSavingBankAccount ? 'Saving...' : 'Save' }}
                    </button>
                    <button
                        type="button"
                        class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        @click="showBankAccountSelector = false"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>

        <!-- Payment Activity -->
        <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 class="mb-4 text-base font-semibold text-gray-900">Payment Activity</h3>
            <div class="flex flex-wrap gap-3">
                <!-- Ledger -->
                <Link
                    v-if="hasLeaseTerm"
                    :href="route('rented-properties.payments', rentPaymentData.rent_info.id)"
                    class="flex items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                >
                    <font-awesome-icon :icon="faBookOpen" class="h-4 w-4 text-gray-500" />
                    Ledger
                </Link>
                <span
                    v-else
                    class="flex cursor-not-allowed select-none items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-400 opacity-60"
                >
                    <font-awesome-icon :icon="faBookOpen" class="h-4 w-4" />
                    Ledger
                </span>

                <!-- Transactions -->
                <Link
                    :href="route('rented-properties.property-transactions', property.id)"
                    class="flex items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                >
                    <font-awesome-icon :icon="faArrowsUpDown" class="h-4 w-4 text-gray-500" />
                    Transactions
                </Link>

                <!-- Payouts -->
                <Link
                    :href="route('sweeps.index') + '?property_id=' + property.id"
                    class="flex items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                >
                    <font-awesome-icon :icon="faMoneyCheckDollar" class="h-4 w-4 text-gray-500" />
                    Payouts
                </Link>

                <!-- History -->
                <Link
                    :href="route('rented-properties.lease-history', property.id)"
                    class="flex items-center gap-2.5 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                >
                    <font-awesome-icon :icon="faClockRotateLeft" class="h-4 w-4 text-gray-500" />
                    History
                </Link>

                <!-- Pause Rent Collection -->
                <button
                    v-if="leaseTermStatus === 'Active'"
                    type="button"
                    :disabled="isSharedViewOnly"
                    class="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm hover:bg-red-100 disabled:pointer-events-none disabled:opacity-50"
                    @click="openStopRentPaymentsModal()"
                >
                    <font-awesome-icon icon="fa-ban" class="h-4 w-4 shrink-0 text-red-500" />
                    <div class="text-left">
                        <div class="flex items-center gap-1">
                            <p class="font-semibold text-red-600">Pause Rent Collection</p>
                            <ToolTip
                                title="Stop Accepting Rent Payments"
                                description="If you no longer wish to receive rental payments or want to block your tenant from paying additional payments, click this button."
                            />
                        </div>
                        <p class="text-xs text-red-500">Temporarily stop collecting rent</p>
                    </div>
                </button>

                <!-- Resume Rent Collection -->
                <button
                    v-if="leaseTermStatus === 'Rent Payments Stopped'"
                    type="button"
                    :disabled="isSharedViewOnly"
                    class="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm hover:bg-green-100 disabled:pointer-events-none disabled:opacity-50"
                    @click="openResumeRentPaymentsModal()"
                >
                    <font-awesome-icon icon="fa-rotate-right" class="h-4 w-4 shrink-0 text-green-500" />
                    <div class="text-left">
                        <p class="font-semibold text-green-700">Resume Rent Collection</p>
                        <p class="text-xs text-green-600">Start collecting rent again</p>
                    </div>
                </button>
            </div>
        </div>

        <!-- Modals -->
        <StopRentPaymentsModal
            :show="showStopRentPaymentsModal"
            :property-address="property.full_address"
            @close="showStopRentPaymentsModal = false"
            @confirm="stopRentPayments"
        />

        <ResumeRentPaymentsModal
            :show="showResumeRentPaymentsModal"
            :property-address="property.full_address"
            @close="showResumeRentPaymentsModal = false"
            @confirm="resumeRentPayments"
        />

        <ResendInvitation
            :show="showResendInvitationModal"
            @close="showResendInvitationModal = false"
            @confirm="resendInvitation"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { router, Link } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
    faMoneyCheckDollar,
    faClockRotateLeft,
    faBuildingColumns,
    faArrowRight,
    faLock,
    faCircleCheck,
    faCircleExclamation,
    faArrowRightArrowLeft,
    faCalendarDays,
    faBookOpen,
    faArrowsUpDown,
    faEnvelope,
} from '@fortawesome/pro-solid-svg-icons'
import StopRentPaymentsModal from '@/Pages/RentedProperties/Components/StopRentPaymentsModal.vue'
import ResumeRentPaymentsModal from '@/Pages/RentedProperties/Components/ResumeRentPaymentsModal.vue'
import ResendInvitation from '@/Shared/ResendInvitation.vue'
import ToolTip from '@/Shared/ToolTip.vue'

const route = (typeof window !== 'undefined' && window.route) || ((name, ...params) => {
    console.warn('Route function not available')
    return '#'
})

const props = defineProps({
    property: {
        type: Object,
        required: true,
    },
    tab: {
        type: String,
        default: 'rent-payments',
    },
    tabData: {
        type: Object,
        default: () => ({}),
    },
    propertyAccess: {
        type: String,
        default: 'owner',
    },
    viewOnlyTooltip: {
        type: Object,
        default: null,
    },
    isScreeningOnly: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['request-upgrade'])

// RAC-10336: the gate is the property's own rent-payments access rather than its
// screening/managed tier. 86bcd9hg3: rent payments are part of RiskGuard Pro, so without
// access the property page opens its Pro pop-up right here (as the Maintenance tab does).
const hasRentPaymentsEntitlement = computed(() => props.tabData?.subscription_data?.has_access === true)

const onConnectBank = (event) => {
    if (!hasRentPaymentsEntitlement.value) {
        event.preventDefault()
        emit('request-upgrade')
    }
}

const onboardingSteps = [
    {
        title: 'Connect your bank',
        description: 'Securely connect through Plaid in minutes.',
    },
    {
        title: 'Invite your tenant',
        description: "We'll send them an invite to set up payments.",
    },
    {
        title: 'Get paid on time',
        description: 'Track payments and manage everything in one place.',
    },
]

const rentPaymentData = computed(() => props.tabData?.rent_payment_data || null)
const isSharedViewOnly = computed(() => props.propertyAccess === 'shared_view')
const hasLeaseTerm = computed(() => Boolean(rentPaymentData.value?.rent_info))
const leaseTermStatus = computed(() => rentPaymentData.value?.rent_info?.status ?? null)
const isCompletedLeaseTerm = computed(() => leaseTermStatus.value === 'Complete')
const hasEditableLeaseTerm = computed(() => hasLeaseTerm.value && !isCompletedLeaseTerm.value)
const isPastDue = computed(() => props.tabData?.is_past_due || false)
const pastDueAmount = computed(() => props.tabData?.past_due_amount || 0)
const isBankVerified = computed(() => props.tabData?.transfer_diligence_status === 'approved')

const agentBankAccounts = computed(() => props.tabData?.agent_bank_accounts || [])
const currentBankAccountId = computed(() => props.tabData?.bank_account?.id || null)
const currentBankAccount = computed(() => {
    if (!currentBankAccountId.value) { return null }
    return agentBankAccounts.value.find(a => a.id === currentBankAccountId.value)
        || props.tabData?.bank_account
        || null
})

const tenantInitials = computed(() => {
    const name = rentPaymentData.value?.tenant?.name
    if (name) {
        const parts = name.trim().split(/\s+/)
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        }
        return parts[0].slice(0, 2).toUpperCase()
    }
    const email = rentPaymentData.value?.invitation?.email
    if (email) { return email[0].toUpperCase() }
    return '?'
})

const selectedBankAccountId = ref(currentBankAccountId.value)
const isSavingBankAccount = ref(false)
const showBankAccountSelector = ref(!currentBankAccountId.value)

watch(currentBankAccountId, (newVal) => {
    selectedBankAccountId.value = newVal
    showBankAccountSelector.value = !newVal
})

const updateBankAccount = () => {
    if (isSharedViewOnly.value) { return }
    if (!selectedBankAccountId.value || selectedBankAccountId.value === currentBankAccountId.value) { return }
    isSavingBankAccount.value = true
    router.post(route('properties.update-bank-account', props.property.id), {
        user_bank_account_id: selectedBankAccountId.value,
    }, {
        preserveScroll: true,
        preserveState: true,
        only: ['tabData', 'tab'],
        onFinish: () => {
            isSavingBankAccount.value = false
            showBankAccountSelector.value = false
        },
    })
}

const showStopRentPaymentsModal = ref(false)
const showResumeRentPaymentsModal = ref(false)
const showResendInvitationModal = ref(false)
const selectedLeaseTermId = ref(null)

const openStopRentPaymentsModal = () => {
    if (isSharedViewOnly.value) { return }
    showStopRentPaymentsModal.value = true
}

const openResumeRentPaymentsModal = () => {
    if (isSharedViewOnly.value) { return }
    showResumeRentPaymentsModal.value = true
}

const showResendInvitationPopup = (leaseTermId) => {
    if (isSharedViewOnly.value) { return }
    selectedLeaseTermId.value = leaseTermId
    showResendInvitationModal.value = true
}

const stopRentPayments = () => {
    if (isSharedViewOnly.value) { return }
    if (!rentPaymentData.value?.rent_info?.id) { return }
    router.post(route('rented-properties.stop-rent-payments', rentPaymentData.value.rent_info.id), {}, {
        preserveScroll: true,
        preserveState: true,
        only: ['tabData', 'tab'],
        onFinish: () => {
            showStopRentPaymentsModal.value = false
        },
    })
}

const resumeRentPayments = () => {
    if (isSharedViewOnly.value) { return }
    if (!rentPaymentData.value?.rent_info?.id) { return }
    router.post(route('rented-properties.resume-rent-payments', rentPaymentData.value.rent_info.id), {}, {
        preserveScroll: true,
        preserveState: true,
        only: ['tabData', 'tab'],
        onFinish: () => {
            showResumeRentPaymentsModal.value = false
        },
    })
}

const resendInvitation = () => {
    if (isSharedViewOnly.value) { return }
    if (!selectedLeaseTermId.value) { return }
    router.post(route('rent-payments.resend-invitation', selectedLeaseTermId.value), {}, {
        preserveScroll: true,
        preserveState: true,
        only: ['tabData', 'tab', 'flash'],
        onFinish: () => {
            showResendInvitationModal.value = false
            selectedLeaseTermId.value = null
        },
    })
}
</script>
